import DOMPurify from 'dompurify';

type DomPurifyInstance = {
  sanitize: (dirty: string, config?: Record<string, unknown>) => string;
};

function unwrapDomPurifyImport(mod: unknown): DomPurifyInstance | ((root?: Window) => DomPurifyInstance) {
  const m = mod as { default?: unknown };
  const inner = m && typeof m === 'object' && 'default' in m ? m.default : mod;
  return inner as DomPurifyInstance | ((root?: Window) => DomPurifyInstance);
}

function sanitizeHtmlWithoutDom(html: string): string {
  return html
    .replace(/<script\b[\s\S]*?<\/script>/gi, '')
    .replace(/<script\b[^>]*\/>/gi, '')
    .replace(/\s+on\w+\s*=\s*(['"])[\s\S]*?\1/gi, '')
    .replace(/\s+on\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript:/gi, '');
}

const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

function balanceHtmlTags(html: string): string {
  const openTags: string[] = [];
  const tagRe = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*\/?>/g;

  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(html)) !== null) {
    const full = m[0];
    const tag = m[1].toLowerCase();

    if (VOID_ELEMENTS.has(tag) || full.endsWith('/>')) continue;

    if (full.charAt(1) === '/') {
      const idx = openTags.lastIndexOf(tag);
      if (idx !== -1) openTags.splice(idx);
    } else {
      openTags.push(tag);
    }
  }

  let result = html;
  for (let i = openTags.length - 1; i >= 0; i--) {
    result += `</${openTags[i]}>`;
  }
  return result;
}

let domPurifyResolved: DomPurifyInstance | undefined;

function isPurifier(x: unknown): x is DomPurifyInstance {
  return typeof x === 'object' && x !== null && typeof (x as DomPurifyInstance).sanitize === 'function';
}

function getDomPurify(): DomPurifyInstance {
  if (domPurifyResolved) return domPurifyResolved;

  const candidate = unwrapDomPurifyImport(DOMPurify);
  if (isPurifier(candidate)) {
    domPurifyResolved = candidate;
    return domPurifyResolved;
  }

  if (typeof window !== 'undefined') {
    const fromWindow = (candidate as (root?: Window) => unknown)(window);
    if (isPurifier(fromWindow)) {
      domPurifyResolved = fromWindow;
      return domPurifyResolved;
    }
  }

  domPurifyResolved = { sanitize: (dirty: string) => balanceHtmlTags(sanitizeHtmlWithoutDom(dirty)) };
  return domPurifyResolved;
}

const domPurifyConfig = {
  ALLOWED_TAGS: [
    'a',
    'p',
    'br',
    'strong',
    'em',
    'u',
    'del',
    'ins',
    'sup',
    'sub',
    'small',
    'big',
    'i',
    'b',
    'u',
    's',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'code',
    'pre',
    'span',
    'div',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'caption',
    'colgroup',
    'col',
  ],
  ALLOWED_ATTR: [
    'class',
    'id',
    'style',
    'title',
    'alt',
    'href',
    'target',
    'rel',
    'width',
    'height',
    'colspan',
    'rowspan',
    'scope',
    'align',
    'valign',
  ],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  // Additional configurations to improve processing
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false,
  // Avoid generating unnecessary empty elements
  WHOLE_DOCUMENT: false,
  SANITIZE_DOM: true,
  // Configure hooks to clean empty content
  HOOKS: {
    uponSanitizeElement: (node: any, data: any) => {
      // Remove empty paragraphs or those containing only spaces
      if (data.tagName === 'p') {
        const textContent = node.textContent || '';
        const innerHTML = node.innerHTML || '';

        // Remove if empty or only contains spaces/&nbsp;
        if (
          !textContent.trim() ||
          textContent.trim() === '' ||
          innerHTML.trim() === '&nbsp;' ||
          innerHTML.trim() === '' ||
          /^\s*$/.test(textContent) ||
          /^\s*&nbsp;\s*$/.test(innerHTML)
        ) {
          node.remove();
        }
      }

      // Remove empty list items
      if (data.tagName === 'li') {
        const textContent = node.textContent || '';
        if (!textContent.trim() || textContent.trim() === '') {
          node.remove();
        }
      }
    },
  },
};

/**
 * Sanitize HTML using DOMPurify with predefined configuration for rich text
 * @param html - The HTML to sanitize
 * @returns Sanitized and safe HTML
 */
export const sanitizeHTML = (html: string): string => {
  if (!html) return '';

  // Clean HTML before sanitizing - more aggressive
  const cleanHtml = balanceHtmlTags(html)
    // Remove completely empty paragraphs
    .replace(/<p>\s*<\/p>/g, '')
    // Remove paragraphs that only contain &nbsp; or spaces
    .replace(/<p>\s*&nbsp;\s*<\/p>/g, '')
    // Remove paragraphs that only contain whitespace
    .replace(/<p>\s+<\/p>/g, '')
    // Remove paragraphs that only contain spaces and &nbsp;
    .replace(/<p>[\s&nbsp;]*<\/p>/g, '')
    // Remove multiple whitespace
    .replace(/\s+/g, ' ')
    // Remove spaces at beginning and end
    .trim();

  if (!cleanHtml) return '';

  const sanitized = getDomPurify().sanitize(cleanHtml, domPurifyConfig);

  // Clean HTML after sanitizing also
  let finalHtml = typeof sanitized === 'string' ? sanitized : '';

  // Apply additional cleaning after sanitization
  finalHtml = finalHtml
    .replace(/<p>\s*<\/p>/g, '')
    .replace(/<p>\s*&nbsp;\s*<\/p>/g, '')
    .replace(/<p>\s+<\/p>/g, '')
    .replace(/<p>[\s&nbsp;]*<\/p>/g, '')
    .trim();

  return finalHtml;
};

/**
 * Sanitize product descriptions specifically
 * @param description - The product description to sanitize
 * @returns Sanitized and safe description
 */
export const sanitizeProductDescription = (description: string): string => {
  return sanitizeHTML(description);
};

export default sanitizeHTML;
